import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Popover, Transition } from "app/headlessui";
import Link from "components/Link";
import React, { FC, Fragment, useState } from "react";
import { Route } from "routers/types";

export interface NavItemType {
  id: string;
  name: string;
  href: Route;
  targetBlank?: boolean;
  children?: NavItemType[];
  type?: "dropdown" | "megaMenu" | "none";
  isNew?: boolean;
  description?: string;
  icon?: string;
  title?: string;
  subTitle?: string;
}

export interface NavigationItemProps {
  menuItem: NavItemType;
  color?: "light" | "dark"; // Add the color prop
}

const NavigationItem: FC<NavigationItemProps> = ({ menuItem, color = "dark" }) => {
  const [menuCurrentHovers, setMenuCurrentHovers] = useState<string[]>([]);

  const onMouseEnterMenu = (id: string) => {
    setMenuCurrentHovers((state) => [...state, id]);
  };

  const onMouseLeaveMenu = (id: string) => {
    setMenuCurrentHovers((state) => state.filter((item, index) => item !== id && index < state.indexOf(id)));
  };

  const textColor = color === "light" ? "text-white hover:text-gray-300" : "text-black hover:text-gray-700";
  const iconColor = color === "light" ? "text-white" : "text-black";

  const renderMainItem = (item: NavItemType) => {
    return (
      <div className="h-20 flex-shrink-0 flex items-center">
        <Link
          className={`inline-flex items-center text-sm lg:text-[15px] font-medium py-2.5 px-4 xl:px-5 rounded-full ${textColor}`}
          href={{
            pathname: item.href || undefined,
          }}
        >
          {item.name}
          {item.type && <ChevronDownIcon className={`ml-1 -mr-1 h-4 w-4 ${iconColor}`} aria-hidden="true" />}
        </Link>
      </div>
    );
  };

  // ===================== MENU MEGAMENU =====================
  const renderMegaMenu = (menu: NavItemType) => {
    if (!menu.children) {
      return null;
    }

    return (
      <li className="menu-item flex-shrink-0 menu-megamenu menu-megamenu--large">
        {renderMainItem(menu)}

        <div className="max-w-4xl mx-auto invisible sub-menu absolute top-full inset-x-0 transform z-50">
          <div className="bg-slate-900/80 backdrop-blur-lg mt-4 shadow-lg flex rounded-2xl min-h-60 justify-center items-center">
            <div className="py-4">
              <div className="grid grid-cols-3 gap-8 px-6">
                {menu.children.map((item) => (
                  <div className="flex flex-col justify-between last:border-r-0 border-r border-slate-700 gap-12">
                    <div className="flex-grow  pt-2">
                      <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-purple-600">{item.name}</h3>
                      <p className="text-gray-100 text-sm mb-4">{item.description || "Discover how our solutions can help your business grow"}</p>
                    </div>
                    <Link
                      key={item.id}
                      href={{
                        pathname: item.href || undefined,
                      }}
                      className="group flex flex-col justify-between "
                    >
                      <div className="flex py-2 items-start gap-2 rounded-2xl">
                        <div className="w-10 h-10 m-1 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                          </svg>
                        </div>
                        <div className="flex flex-col gap-0">
                          <h3 className="text-md font-semibold text-white group-hover:text-purple-600">{item.name}</h3>
                          {/* <p className="text-gray-100 text-xs mb-4">{item.description || "Discover how our solutions can help your business grow"}</p> */}
                          <div className="flex items-center text-purple-600 text-sm font-medium">
                            Learn more
                            <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </li>
    );
  };

  const renderMegaMenuNavlink = (item: NavItemType) => {
    return (
      <li key={item.id} className={`${item.isNew ? "menuIsNew" : ""}`}>
        <Link
          className="font-normal text-slate-600 hover:text-black dark:text-slate-400 dark:hover:text-white "
          href={{
            pathname: item.href || undefined,
          }}
        >
          {item.name}
        </Link>
      </li>
    );
  };

  const renderDropdownMenu = (menuDropdown: NavItemType) => {
    const isHover = menuCurrentHovers.includes(menuDropdown.id);
    return (
      <Popover as="li" className="menu-item menu-dropdown relative" onMouseEnter={() => onMouseEnterMenu(menuDropdown.id)} onMouseLeave={() => onMouseLeaveMenu(menuDropdown.id)}>
        {() => (
          <>
            <Popover.Button as={Fragment}>{renderMainItem(menuDropdown)}</Popover.Button>
            <Transition
              as={Fragment}
              show={isHover}
              enter="transition ease-out duration-150"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel static className="sub-menu absolute transform z-10 w-56 top-full left-0">
                <ul className="rounded-2xl shadow-lg ring-1 ring-black ring-opacity-5 bg-slate-900 py-4 grid space-y-1">
                  {menuDropdown.children?.map((item) => (
                    <li key={item.id}>{renderMainItem(item)}</li>
                  ))}
                </ul>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  switch (menuItem.type) {
    case "dropdown":
      return renderDropdownMenu(menuItem);
    case "megaMenu":
      return renderMegaMenu(menuItem);
    default:
      return <li className="menu-item flex-shrink-0">{renderMainItem(menuItem)}</li>;
  }
};

export default NavigationItem;
